import { Button, message, Tag } from 'antd';
import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useContext } from 'react';
import { UserContext } from '../../UserContext';
import { BaseComponentProps } from '../../types';
import { formatDateFromUTCtoAgent, getAgentTimezone } from './utils';

interface SupportTicketsProps extends BaseComponentProps {
  onReopen?: () => void;
  tickets: SupportTicket[];
}

interface ConversationMessageProps {
  message: {
    content: string;
    author: string;
    timestamp: string;
    isAgent: boolean;
  };
  formatMessageTime: (timestamp: string) => string;
}

interface ConversationNote {
  id: string;
  content: string;
  created_at: string;
  user?: {
    summary?: string;
    email?: string;
  };
}

interface SupportTicket {
  id: string;
  incident_number: number;
  description?: string;
  status?: string;
  created_at?: string;
  resolved_at?: string;
  acknowledged_at?: string;
  assignee?: string;
  escalated_to?: string;
  resolution?: string;
  notes?: string;
  resolution_notes?: string;
  linked_message_id?: string;
  conversation_thread?: ConversationNote[];
  source_id?: string;
}

const ConversationMessage = ({
  message,
  formatMessageTime,
}: ConversationMessageProps): JSX.Element => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 10px',
        background: message.isAgent ? '#f0f7ff' : '#fafafa',
        borderRadius: '4px',
        borderLeft: `3px solid ${message.isAgent ? '#1890ff' : '#52c41a'}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: '12px',
            color: message.isAgent ? '#1890ff' : '#52c41a',
          }}
        >
          {message.isAgent ? '👤' : '🛠️'} {message.author}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: '#999',
          }}
        >
          {formatMessageTime(message.timestamp)}
        </span>
      </div>
      <div
        style={{
          color: '#333',
          whiteSpace: 'pre-wrap',
          lineHeight: '1.5',
          fontSize: '13px',
        }}
      >
        {message.content}
      </div>
    </div>
  );
};

export const SupportTickets = ({
  agent,
  apiUrl,
  clientConfig,
  onReopen,
  tickets,
}: SupportTicketsProps): JSX.Element => {
  const user = useContext(UserContext);
  const agentTimezone = getAgentTimezone(agent, clientConfig);

  // Use tickets from prop (loaded in parent)

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Resolved':
        return '#52c41a';
      case 'Acknowledged':
        return '#1890ff';
      case 'Triggered':
        return '#faad14';
      default:
        return '#d9d9d9';
    }
  };

  const getClosedBy = (ticket: SupportTicket): string => {
    if (ticket.status === 'Resolved') {
      if (ticket.assignee) {
        return ticket.assignee;
      }
      return 'Support Team';
    }
    return '';
  };

  const formatMessageTime = (timestamp: string) => {
    return formatDateFromUTCtoAgent(timestamp, agentTimezone);
  };

  const getMessageAuthor = (note: ConversationNote): string => {
    if (note.user?.summary) {
      return note.user.summary;
    }
    if (note.user?.email) {
      return note.user.email.split('@')[0];
    }
    return 'Support';
  };

  const isAgentMessage = (note: ConversationNote): boolean => {
    const author = getMessageAuthor(note).toLowerCase();
    const agentName = `${agent.first_name} ${agent.last_name}`.toLowerCase();
    return (
      author.includes(agentName) ||
      author.includes(agent.email?.toLowerCase() || '')
    );
  };

  const handleAcknowledgeIncident = async (ticket: SupportTicket) => {
    if (!user?.accessToken || !user?.name) {
      message.error('Missing required information to acknowledge incident');
      return;
    }

    // Use source_id if available, otherwise fall back to incident_number
    const incidentIdentifier = ticket.source_id || ticket.incident_number;

    if (!incidentIdentifier) {
      message.error('Ticket identifier is missing. Cannot acknowledge.');
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${incidentIdentifier}/acknowledge`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            email: user.name,
          }),
        },
      );

      if (response.ok) {
        message.success('Incident acknowledged successfully');
        await new Promise(resolve => setTimeout(resolve, 500));
        if (onReopen) {
          onReopen();
        }
      } else {
        const errorText = await response.text();
        console.error(
          'Failed to acknowledge incident:',
          response.status,
          errorText,
        );
        message.error('Failed to acknowledge incident');
      }
    } catch (error) {
      console.error('Failed to acknowledge incident:', error);
      message.error('Failed to acknowledge incident');
    }
  };

  const handleReopenIncident = async (incidentNumber: number) => {
    if (!user?.accessToken || !user?.name) {
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${incidentNumber}/reopen`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            reopen_note: 'Reopened via Agent Portal',
            email: user.name,
          }),
        },
      );

      if (response.ok) {
        message.success('Incident reopened successfully');
        await new Promise(resolve => setTimeout(resolve, 500));
        if (onReopen) {
          onReopen();
        }
      } else {
        throw new Error('Failed to reopen incident');
      }
    } catch (error) {
      console.error('Failed to reopen incident:', error);
      message.error('Failed to reopen incident');
    }
  };

  if (tickets.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        No support tickets found for this agent.
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>Support Tickets</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {tickets.map(record => {
          const closedBy = getClosedBy(record);
          const conversationThread = record.conversation_thread || [];

          // Build conversation from description and notes
          const conversationMessages: Array<{
            content: string;
            author: string;
            timestamp: string;
            isAgent: boolean;
          }> = [];

          // Add initial description as first message from agent
          if (record.description) {
            conversationMessages.push({
              content: record.description,
              author: `${agent.first_name} ${agent.last_name}`,
              timestamp: record.created_at || '',
              isAgent: true,
            });
          }

          // Add notes from conversation thread
          conversationThread.forEach(note => {
            conversationMessages.push({
              content: note.content,
              author: getMessageAuthor(note),
              timestamp: note.created_at,
              isAgent: isAgentMessage(note),
            });
          });

          // Sort by timestamp
          conversationMessages.sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );

          return (
            <div
              key={record.id}
              style={{
                background: '#fafafa',
                borderRadius: 6,
                padding: '12px',
                border: '1px solid #e8e8e8',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                }}
              >
                <div>
                  <Button
                    onClick={() =>
                      window.open(
                        `https://housewhisper.pagerduty.com/incidents/${record.source_id}`,
                        '_blank',
                      )
                    }
                    style={{
                      padding: 0,
                      fontSize: '14px',
                      fontWeight: 'bold',
                      marginRight: '8px',
                      height: 'auto',
                      textDecoration: 'underline',
                    }}
                    type="link"
                  >
                    #{record.incident_number}
                  </Button>
                  <Tag
                    color={getStatusColor(record.status)}
                    style={{
                      padding: '0px 8px',
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 11,
                      lineHeight: '18px',
                    }}
                  >
                    {record.status || 'Unknown'}
                  </Tag>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {record.status?.toLowerCase() === 'triggered' && (
                    <Button
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleAcknowledgeIncident(record)}
                      size="small"
                      type="default"
                    >
                      Acknowledge
                    </Button>
                  )}
                  {record.status?.toLowerCase() === 'resolved' && (
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() =>
                        handleReopenIncident(record.incident_number)
                      }
                      size="small"
                      type="primary"
                    >
                      Reopen
                    </Button>
                  )}
                </div>
              </div>

              {record.description && (
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ fontSize: '13px' }}>Issue Summary:</strong>
                  <div
                    style={{
                      marginTop: '2px',
                      color: '#555',
                      fontSize: '13px',
                    }}
                  >
                    {record.description}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '8px',
                  marginBottom: '8px',
                  fontSize: '12px',
                }}
              >
                {record.created_at && (
                  <div>
                    <strong>Created:</strong>{' '}
                    <span style={{ color: '#555' }}>
                      {formatMessageTime(record.created_at)}
                    </span>
                  </div>
                )}
                {record.acknowledged_at && (
                  <div>
                    <strong>Ack:</strong>{' '}
                    <span style={{ color: '#555' }}>
                      {formatMessageTime(record.acknowledged_at)}
                    </span>
                  </div>
                )}
                {record.resolved_at && (
                  <div>
                    <strong>Closed:</strong>{' '}
                    <span style={{ color: '#555' }}>
                      {formatMessageTime(record.resolved_at)}
                    </span>
                  </div>
                )}
              </div>

              {record.assignee && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Assignee:</strong>
                  <span style={{ marginLeft: '8px', color: '#555' }}>
                    {record.assignee}
                  </span>
                </div>
              )}

              {record.escalated_to && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Escalated To:</strong>
                  <span style={{ marginLeft: '8px', color: '#555' }}>
                    {record.escalated_to}
                  </span>
                </div>
              )}

              {record.resolution && (
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ fontSize: '13px' }}>Resolution:</strong>
                  <div
                    style={{
                      marginTop: '4px',
                      padding: '8px',
                      background: '#e6f7ff',
                      borderRadius: 4,
                      color: '#555',
                      fontSize: '13px',
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{record.resolution}</div>
                    {record.linked_message_id && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: '0.9em',
                          color: '#777',
                        }}
                      >
                        Linked: {record.linked_message_id}
                      </div>
                    )}
                    {record.resolution_notes && (
                      <div
                        style={{
                          marginTop: 4,
                          fontStyle: 'italic',
                          fontSize: '12px',
                        }}
                      >
                        {record.resolution_notes}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {conversationMessages.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '13px',
                    }}
                  >
                    Conversation ({conversationMessages.length}):
                  </strong>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      padding: '8px',
                      background: '#fff',
                      border: '1px solid #e8e8e8',
                      borderRadius: 4,
                    }}
                  >
                    {conversationMessages.map((message, index) => (
                      <ConversationMessage
                        formatMessageTime={formatMessageTime}
                        key={index}
                        message={message}
                      />
                    ))}
                    {record.status === 'Resolved' && closedBy && (
                      <div
                        style={{
                          padding: '8px',
                          background: '#f6ffed',
                          borderRadius: '4px',
                          border: '1px solid #b7eb8f',
                          textAlign: 'center',
                          fontWeight: 600,
                          color: '#389e0d',
                          fontSize: '12px',
                        }}
                      >
                        ✅ Closed by {closedBy}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {record.notes && !conversationMessages.length && (
                <div>
                  <strong>Notes:</strong>
                  <div
                    style={{
                      marginTop: '4px',
                      padding: '10px',
                      background: '#fff',
                      border: '1px solid #e8e8e8',
                      borderRadius: 4,
                      whiteSpace: 'pre-wrap',
                      color: '#555',
                    }}
                  >
                    {record.notes}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
